import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'

import {
    Box,
    Link,
    ListItem,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import colors from '../../theme/foundations/colors'
import {
    LocationIcon,
    PhoneIcon,
    ShopIcon,
    StoreOthersIcon,
    StoreOutletIcon,
    StoreVBQIcon,
    WhatsappIcon
} from '../../components/custom-icons'
import {buildGoogleItineraryUrl, getStoreUrl} from '../../utils/url-utils'
import {formatHourByLocale} from '../../utils/utils'
import useMultiSite from '../../hooks/use-multi-site'
import {STORE_STATUSES} from '@app/constants'

/**
 * StoreCard - A React component that displays information about a store.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.store - The store data.
 * @param {string} props.store.id - The unique identifier for the store.
 * @param {string} props.store.city - The city where the store is located.
 * @param {string} props.store.address1 - The primary address of the store.
 * @param {string} [props.store.address2] - The secondary address of the store.
 * @param {string} props.store.postalCode - The postal code of the store.
 * @param {string} props.store.countryCode - The country code of the store.
 * @param {string} props.store.phone - The contact phone number of the store.
 * @returns {JSX.Element} The rendered component.
 */
const StoreCard = ({store}) => {
    const {formatMessage} = useIntl()
    const [isStoreOpened, setIsStoreOpened] = useState(false)
    const [isStoreTemporarilyClosed, setIsStoreTemporarilyClosed] = useState(false)
    const [storeHours, setStoreHours] = useState('')
    const {locale} = useMultiSite()
    /**
     * Determines the store's opening status and hours for the current day.
     *
     * This function parses the store's opening hours from a JSON string, checks the current day,
     * and updates the store's open status and hours accordingly. If the store is closed today,
     * or temporarily/seasonally closed, it sets the store's open status to false.
     * Otherwise, it sets the store's open status to true and updates the store hours.
     *
     * @function
     * @returns {void}
     */
    const handleStoreHours = () => {
        const openingHours = store.c_storeOpeningHours && JSON.parse(store.c_storeOpeningHours)
        const today = new Date().toLocaleString('en', {weekday: 'long'}).toLowerCase()
        const isFutureOpening =
            store?.c_openingDate && new Date(store.c_openingDate).getTime() > Date.now()
        const isPastClosed =
            store?.c_closingDate && new Date(store.c_closingDate).getTime() < Date.now()

        if (openingHours?.[today]) {
            const todayHours = openingHours[today]

            setIsStoreTemporarilyClosed(store.c_temporaryClosed)
            if (
                todayHours.isClosed ||
                store.c_temporaryClosed ||
                store.c_seasonallyClosed ||
                isFutureOpening ||
                isPastClosed
            ) {
                setIsStoreOpened(false)
            } else {
                setIsStoreOpened(true)

                const storeHoursIntervals = todayHours.openIntervals
                    ?.map((interval) => {
                        const start = interval.start
                        const end = interval.end

                        return start && end
                            ? `${formatHourByLocale(locale.id, start)} - ${formatHourByLocale(
                                  locale.id,
                                  end
                              )}`
                            : null
                    })
                    .filter(Boolean)
                    .join('; ')

                setStoreHours(storeHoursIntervals)
            }
        }
    }

    useEffect(() => {
        handleStoreHours()
    }, [store.c_storeOpeningHours])

    return (
        <ListItem
            data-store={store.id}
            aria-label={formatMessage({
                id: 'stores_page.store',
                defaultMessage: 'Store'
            })}
            p={'16px'}
            mb={'16px'}
            mt={{base: '16px', lg: 0}}
            border={`1px solid ${colors.borderGray}`}
            display={'flex'}
            role="group"
        >
            <Box marginRight={'5px'}>
                {(() => {
                    switch (store.c_storeType) {
                        case 'VS':
                            return <StoreVBQIcon />
                        case 'VO':
                            return <StoreOutletIcon />
                        case 'OS':
                            return <StoreOthersIcon />
                        default:
                            return <StoreOthersIcon />
                    }
                })()}
            </Box>
            <Box marginTop={'5px'} width={'100%'}>
                <Stack
                    mb={'10px'}
                    direction={isStoreOpened ? 'column' : 'row'}
                    justifyContent={'space-between'}
                >
                    {/* TO UN-COMMENT WHEN THE STORE PAGE WILL BE IMPLEMENTED
                    {store.name && (
                        <Text variant={'bodyBase2'} textTransform={'uppercase'}>
                            <Link href={getStoreUrl(store.id)}>{store.name}</Link>
                        </Text>
                    )}
                    */}
                    {store.name && (
                        <Text
                            variant={'bodyBase2'}
                            textTransform={'uppercase'}
                            role="heading"
                            aria-level="2"
                        >
                            {store.name}
                        </Text>
                    )}
                    <Text variant={'bodyBase2'}>
                        <Box
                            as="span"
                            display={'inline-block'}
                            height={'6px'}
                            width={'6px'}
                            backgroundColor={isStoreOpened ? colors.green : colors.red}
                            borderRadius={'50%'}
                            mb={'1px'}
                            mr={'4px'}
                        ></Box>
                        <Box
                            as={'span'}
                            color={isStoreOpened ? colors.green : colors.red}
                            mr={'2px'}
                        >
                            {isStoreOpened
                                ? formatMessage(STORE_STATUSES.OPEN)
                                : isStoreTemporarilyClosed
                                ? formatMessage(STORE_STATUSES.TEMPORARILY_CLOSED)
                                : formatMessage(STORE_STATUSES.CLOSED)}
                        </Box>
                        {isStoreOpened && (
                            <Box as="span" fontSize={'11px'} colors={colors.darkGray}>
                                {`${formatMessage({
                                    id: 'stores_page.store.today',
                                    defaultMessage: 'today:'
                                })} ${storeHours}`}
                            </Box>
                        )}
                    </Text>
                </Stack>
                <Box mb={'16px'}>
                    <Text variant={'bodySmall'}>{store.address1}</Text>
                    {store.address2 && <Text variant={'bodySmall'}>{store.address2}</Text>}
                    <Text variant={'bodySmall'}>
                        {store.postalCode} {store.city}
                    </Text>
                    <Text variant={'bodySmall'}>{store.countryCode}</Text>
                </Box>
                <Stack direction={'row'} wrap={'wrap'} gap={0}>
                    {store.phone && (
                        <Link
                            mb={'8px'}
                            flex={{base: '0 0 35%', lg: '0 0 25%'}}
                            target="_blank"
                            href={`tel:${store.phone}`}
                            display={'flex'}
                            alignItems={'center'}
                            textTransform={'uppercase'}
                        >
                            <PhoneIcon />
                            <Box as={'span'} ml={'4px'}>
                                {formatMessage({
                                    id: 'stores_page.store.call',
                                    defaultMessage: 'CALL'
                                })}
                            </Box>
                        </Link>
                    )}
                    {store.c_WhatsappLink && (
                        <Link
                            mb={'8px'}
                            flex={{base: '0 0 35%', lg: '0 0 25%'}}
                            target="_blank"
                            href={store.c_WhatsappLink}
                            display={'flex'}
                            alignItems={'center'}
                            textTransform={'uppercase'}
                        >
                            <WhatsappIcon />
                            <Box as={'span'} ml={'4px'}>
                                {formatMessage({
                                    id: 'stores_page.store.discuss',
                                    defaultMessage: 'Discuss'
                                })}
                            </Box>
                        </Link>
                    )}
                    {store.latitude && store.longitude && (
                        <Link
                            mb={'8px'}
                            flex={{base: '0 0 35%', lg: '0 0 25%'}}
                            href={buildGoogleItineraryUrl(store.latitude, store.longitude)}
                            target="_blank"
                            display={'flex'}
                            alignItems={'center'}
                            textTransform={'uppercase'}
                        >
                            <LocationIcon />
                            <Box as={'span'} ml={'4px'}>
                                {formatMessage({
                                    id: 'stores_page.store.itinerary',
                                    defaultMessage: 'itinerary'
                                })}
                            </Box>
                        </Link>
                    )}
                    {/* TO UN-COMMENT WHEN THE STORE PAGE WILL BE IMPLEMENTED
                     <Link
                        mb={'8px'}
                        flex={{base: '0 0 35%', lg: '0 0 25%'}}
                        href={getStoreUrl(store.id)}
                        display={'flex'}
                        alignItems={'center'}
                        textTransform={'uppercase'}
                    >
                        <ShopIcon />
                        <Box as={'span'} ml={'4px'}>
                            {formatMessage({
                                id: 'stores_page.store.mystore',
                                defaultMessage: 'store infos'
                            })}
                        </Box>
                    </Link> */}
                </Stack>
            </Box>
        </ListItem>
    )
}

StoreCard.propTypes = {
    store: PropTypes.object
}

export default StoreCard
